import { useState } from "react";
import Table from "../../Components/Table";
import StatusHandler from "../../Components/Table/StatusHandler";
import { Link } from "react-router-dom";
import useTableControls from "../../Hooks/useTableControls";
import useFetchData from "../../Hooks/useFetchData";
import { getAllUsers, USER_STATUS_FILTER_OPTIONS } from "../../Services/User";
import AccountDeletionBadge, { isAccountDeleted } from "../../Components/AccountDeletionBadge";
import { getSignupRequests } from "../../Services/SignupRequest";
import { useQuery } from "react-query";
import { runAccountStatusChange } from "../../Utils/accountStatusFlow";
import DocumentExpiryBadge from "../../Components/DocumentExpiryBadge";
import { format_date, getSerialNumber, getDriverCoords } from "../../Utils/helpers";
import PageHeader from "../../Components/PageHeader";
import "../Dashboard/Dashboard.css";

export default function MoverManagement() {
  // const sampleData = [
  //   {
  //     sn: "1",
  //     src: "images/avatar2.png",
  //     name: "Mark Carson",
  //     email: "abc@xyz.com",
  //     registeredOn: "01/02/2022",
  //     status: true,
  //   },
  //   {
  //     sn: "2",
  //     src: "images/avatar2.png",
  //     name: "Mark Carson",
  //     email: "abc@xyz.com",
  //     registeredOn: "01/02/2022",
  //     status: false,
  //   },
  //   {
  //     sn: "3",
  //     src: "images/avatar2.png",
  //     name: "Mark Carson",
  //     email: "abc@xyz.com",
  //     registeredOn: "01/02/2022",
  //     status: true,
  //   },
  //   {
  //     sn: "4",
  //     src: "images/avatar2.png",
  //     name: "Mark Carson",
  //     email: "abc@xyz.com",
  //     registeredOn: "01/02/2022",
  //     status: true,
  //   },
  // ];

  const {
    perPage,
    setPerPage,
    status,
    setStatus,
    search_string,
    setSearchString,
    from,
    setFrom,
    to,
    setTo,
  } = useTableControls();

  const {
    // INTERNAL EXPORTS
    page,
    setPage,
    // REACT QUERY EXPORTS
    isFetching,
    isLoading,
    data,
    refetch,
  } = useFetchData("driver_logs", getAllUsers, [
    perPage,
    "driver",
    search_string,
    status,
    from,
    to,
    true
  ]);
  // console.log("driver_logs Data: ", data?.data);

  const { data: signupCountData } = useQuery(
    ["signup_request_pending_count"],
    () => getSignupRequests(1, 1, "", "pending"),
    { staleTime: 60 * 1000 }
  );
  const pendingSignupCount = signupCountData?.data?.data?.totalDocs ?? 0;

  const [loadingStatus, setLoadingStatus] = useState(false);

  return (
    <div className="app-content content">
      <div className="content-wrapper">
        <div className="content-body">
          <section id="configuration">
            <div className="row">
              <div className="col-12">
                <div className="user-details-wrapper">
                  <PageHeader
                    title="Driver Management"
                    subtitle="Review driver applications, documentation, and performance metrics"
                    action={
                      <Link
                        to="/mover-management/signup-requests"
                        className="siteBtn header-btn-standard w-100-mobile signup-requests-btn"
                      >
                        <i className="fas fa-user-plus me-2"></i>
                        Signup Requests
                        {pendingSignupCount > 0 && (
                          <span className="signup-requests-count-badge">
                            {pendingSignupCount}
                          </span>
                        )}
                      </Link>
                    }
                  />

                  <Table
                    key={"mover-management-table"}
                    tableTitles={[
                      "S.No.",
                      "Full Name",
                      "Email Address",
                      "Date Registered",
                      "Status",
                      "Documents",
                      "Availability",
                      "Location",
                      "Action",
                    ]}
                    dateFilter
                    showEntries
                    sortBy
                    isLoading={isLoading}
                    isFetching={isFetching}
                    data={data?.data?.data?.docs}
                    totalPages={Math.ceil(data?.data?.data?.totalPages || 0)}
                    totalDocs={data?.data?.data?.totalDocs}
                    page={page}

                    setPage={setPage}
                    setSearchString={setSearchString}
                    status={status}
                    setStatus={setStatus}
                    status_options={USER_STATUS_FILTER_OPTIONS}
                    perPage={perPage}
                    setPerPage={setPerPage}
                    from={from}
                    setFrom={setFrom}
                    to={to}
                    setTo={setTo}
                  >
                    {data?.data?.data?.docs?.map((item, index) => {
                        const { lat, lng, hasValidCoords } = getDriverCoords(item?.location);
                        return (
                        <tr key={item?._id || index}>
                          <td>{getSerialNumber(data?.data?.data, index)}</td>
                          <td>{item.firstName || <span className="text-muted" style={{ fontSize: '0.85rem' }}>Profile Incomplete</span>}</td>
                          <td>{item.email || <span className="text-muted" style={{ fontSize: '0.85rem' }}>{isAccountDeleted(item) ? 'Removed' : 'Profile Incomplete'}</span>}</td>
                          <td>
                            {item?.createdAt &&
                              format_date(item?.createdAt, "MM/DD/YYYY")}
                          </td>
                          <td>
                            {isAccountDeleted(item) ? (
                              <AccountDeletionBadge item={item} className="" />
                            ) : (
                              <>
                                <span className={`status-pill ${(!item.firstName || !item.email) ? 'status-pending' : (item.active ? 'status-active' : 'status-inactive')}`}
                                >
                                  {(!item.firstName || !item.email) ? "Incomplete" : (item.active ? "Active" : "Inactive")}
                                </span>
                                {item?.active && item?.pendingSuspension ? (
                                  <span className="status-pill status-pending ms-1" title="Will be suspended when the current delivery ends">
                                    Suspension scheduled
                                  </span>
                                ) : null}
                                <AccountDeletionBadge item={item} />
                              </>
                            )}
                          </td>
                          <td>
                            <DocumentExpiryBadge expiry={item?.expiry} documentsExpired={item?.documentsExpired} />
                          </td>
                          <td>
                            <span
                              className={`driver-availability-badge driver-availability-badge--sm ${
                                item?.online ? "is-online" : "is-offline"
                              }`}
                            >
                              <span className="driver-availability-dot" aria-hidden="true" />
                              {item?.online ? "Online" : "Offline"}
                            </span>
                          </td>
                          <td>
                            {hasValidCoords ? (
                                <a href={`https://www.google.com/maps?q=${lat},${lng}`} target="_blank" rel="noopener noreferrer" className="driver-location-link" style={{ fontSize: '0.85rem' }}>
                                    {lat.toFixed(4)}, {lng.toFixed(4)}
                                </a>
                            ) : (
                                <span className="text-muted" style={{ fontSize: '0.85rem' }}>N/A</span>
                            )}
                          </td>
                          <td>
                            <StatusHandler
                              key={`${item?._id}-status`}
                              view
                              suspend={!isAccountDeleted(item)}
                              item={item}
                              isLoading={loadingStatus}
                              redirectUrl={`/mover-management/mover-details/${item?._id}`}
                              onClick={() =>
                                runAccountStatusChange({
                                  item,
                                  label: "Driver",
                                  onDone: refetch,
                                  setBusy: setLoadingStatus,
                                })
                              }
                            />
                          </td>
                        </tr>
                      )})}
                  </Table>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
      <style>{`
                .fw-800 { font-weight: 800; }
                
                @media (max-width: 576px) {
                    .w-100-mobile { width: 100% !important; text-align: center; justify-content: center; }
                    .user-details-wrapper { padding: 0.5rem; }
                }
            `}</style>
    </div>
  );
}
